import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, Grid, Skeleton, Theme, useTheme } from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import useSubscriberRelogin, { LoginSessionStatePayload } from '../../hooks/useSubscriberRelogin';
import useSubscribers from '../../hooks/useSubscribers';
import StyledCardTitleBar from '../common/StyledCardTitleBar';
import OnlyFansOtpCode from './OnlyFansOtpCode';
import OnlyFansOtpSelection from './OnlyFansOtpSelection';
import OnlyFansReloginEmailPassword from './OnlyFansReloginEmailPassword';
import OnlyFansReloginStatus from './OnlyFansReloginStatus';
import OnlyFansSelfieIDLink from './OnlyFansSelfieIDLink';

type Props = {
    subscriber: any;
    refetchSubscriber: () => void;
    open: boolean;
    onClose: () => void;
};

// Define state shape and initial state
type ReloginState = {
    loginMode: 'email' | 'advanced';
    email: string;
    password: string;
    userAgent: string;
    cookie: string;
    xBc: string;
    selectedOtpOption: string | null;
    otpCode: string | null;
    selfieLink: string | null;
    isReloginSessionInProgress: boolean;
    session: LoginSessionStatePayload | null;
};

const initialState: ReloginState = {
    loginMode: 'email',
    email: '',
    password: '',
    userAgent: '',
    cookie: '',
    xBc: '',
    selectedOtpOption: null,
    otpCode: null,
    selfieLink: null,
    isReloginSessionInProgress: false,
    session: null,
};

// Define reducer actions
type ReloginAction =
    | { type: 'SET_LOGIN_MODE'; payload: 'email' | 'advanced' }
    | { type: 'SET_EMAIL'; payload: string }
    | { type: 'SET_PASSWORD'; payload: string }
    | { type: 'SET_USER_AGENT'; payload: string }
    | { type: 'SET_COOKIE'; payload: string }
    | { type: 'SET_X_BC'; payload: string }
    | { type: 'SET_SELECTED_OTP_OPTION'; payload: string | null }
    | { type: 'SET_OTP_CODE'; payload: string | null }
    | { type: 'SET_SELFIE_LINK'; payload: string | null }
    | { type: 'SET_RELOGIN_SESSION_IN_PROGRESS'; payload: boolean }
    | { type: 'SET_SESSION'; payload: LoginSessionStatePayload | null }
    | { type: 'RESET' };

// Reducer function
const reloginReducer = (state: ReloginState, action: ReloginAction): ReloginState => {
    switch (action.type) {
        case 'SET_LOGIN_MODE':
            return {
                ...state,
                loginMode: action.payload,
                // Clear fields based on login mode
                ...(action.payload === 'email' ? { userAgent: '', cookie: '', xBc: '' } : { email: '', password: '' }),
            };
        case 'SET_EMAIL':
            return { ...state, email: action.payload };
        case 'SET_PASSWORD':
            return { ...state, password: action.payload };
        case 'SET_USER_AGENT':
            return { ...state, userAgent: action.payload };
        case 'SET_COOKIE':
            return { ...state, cookie: action.payload };
        case 'SET_X_BC':
            return { ...state, xBc: action.payload };
        case 'SET_SELECTED_OTP_OPTION':
            return { ...state, selectedOtpOption: action.payload };
        case 'SET_OTP_CODE':
            return { ...state, otpCode: action.payload };
        case 'SET_SELFIE_LINK':
            return { ...state, selfieLink: action.payload };
        case 'SET_RELOGIN_SESSION_IN_PROGRESS':
            return { ...state, isReloginSessionInProgress: action.payload };
        case 'SET_SESSION':
            return { ...state, session: action.payload };
        case 'RESET':
            return { ...initialState };
        default:
            return state;
    }
};

const OnlyFansReloginDialog = (props: Props) => {
    const { subscriber, refetchSubscriber, open, onClose } = props;
    const theme: Theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();

    // Subscribers
    const { refetch: refetchSubscribers } = useSubscribers();

    // Use reducer for state management
    const [state, dispatch] = useReducer(reloginReducer, initialState);

    // Destructure state for readability
    const {
        loginMode,
        email,
        password,
        userAgent,
        cookie,
        xBc,
        selectedOtpOption,
        otpCode,
        selfieLink,
        isReloginSessionInProgress,
        session,
    } = state;

    // Relogin hook
    const {
        data: subscriberRelogin,
        isLoading: isSubscriberReloginLoading,
        refetch: refetchSubscriberRelogin,
        subscriberReloginStart,
        isSubscriberReloginStartSubmitting,
        subscriberReloginCancel,
        subscriberReloginRequestOtpCode,
        subscriberReloginSubmitOtpCode,
    } = useSubscriberRelogin(subscriber._id, isReloginSessionInProgress);

    // Reset the form when dialog closes
    useEffect(() => {
        if (!open) {
            dispatch({ type: 'RESET' });

            // If there was an active session, cancel it when closing
            if (session || isReloginSessionInProgress) {
                subscriberReloginCancel(subscriber._id).catch(() => {
                    // Silently handle error on close
                });
            }
        }
    }, [open, session, isReloginSessionInProgress, subscriber._id, subscriberReloginCancel]);

    // Handle error display
    const showError = useCallback(
        (title: string, message: string, closeAfter = false) => {
            return dialog
                .alert({
                    title,
                    message,
                })
                .then(() => {
                    if (closeAfter) {
                        setTimeout(() => {
                            handleCloseDialog(true);
                        }, 500);
                    }
                });
        },
        [dialog],
    );

    // Handle dialog close
    const handleCloseDialog = useCallback(
        (skipConfirmation: boolean) => {
            // console.log('handleCloseDialog', skipConfirmation);
            // console.log('subscriberRelogin', subscriberRelogin);
            // console.log('isReloginSessionInProgress', isReloginSessionInProgress);
            // console.log('session', session);

            if (isReloginSessionInProgress) {
                if (skipConfirmation) {
                    subscriberReloginCancel(subscriber._id).then(response => {
                        if (response.success) {
                            dispatch({ type: 'RESET' });
                            onClose();
                        } else {
                            showError('Error', 'Failed to cancel relogin session. Please try again or contact Admin.');
                        }
                    });
                } else {
                    dialog
                        .confirm({
                            title: 'Warning',
                            message: 'Relogin session is still in progress. Are you sure you want to cancel?',
                        })
                        .then(() => {
                            subscriberReloginCancel(subscriber._id).then(response => {
                                if (response.success) {
                                    dispatch({ type: 'RESET' });
                                    onClose();
                                } else {
                                    showError('Error', 'Failed to cancel relogin session. Please try again or contact Admin.');
                                }
                            });
                        })
                        .catch(() => {
                            /* User canceled */
                        });
                }
            } else {
                // if (session) {
                subscriberReloginCancel(subscriber._id)
                    .then(response => {
                        if (response.success) {
                            dispatch({ type: 'RESET' });
                            onClose();
                        }
                    })
                    .catch(() => {
                        /* Handle error */
                    });
                // }

                onClose();
            }
        },
        [dialog, isReloginSessionInProgress, onClose, session, subscriber._id, subscriberReloginCancel, showError],
    );

    // Handle session result
    const handleSessionResult = useCallback(
        (result: string | null | undefined) => {
            if (!result) return false;

            const errorMessages: Record<string, string> = {
                wrong_password: 'Wrong email or password. Please try again.',
                wrong_email: 'Wrong email or password. Please try again.',
                wrong_user: 'Login details do not match this account. Please try again.',
                invalid_credentials: 'Invalid session values (User-Agent, Cookie or x-bc). Please try again.',
                access_denied: 'Invalid session values (User-Agent, Cookie or x-bc). Please try again.',
                captcha_fail: 'Could not login to OnlyFans. Please wait a minute and try again, or contact Admin.',
                error: 'Could not login to OnlyFans. Please wait a minute and try again, or contact Admin.',
                failed: 'Could not login to OnlyFans. Please wait a minute and try again, or contact Admin.',
                timeout: 'Could not login to OnlyFans. Please wait a minute and try again, or contact Admin.',
                unknown_subscriber: 'Subscriber not found. Please try again or contact Admin.',
                selfie_expired: 'Selfie ID link has expired. Please try again.',
            };

            if (result === 'success') {
                dispatch({ type: 'SET_RELOGIN_SESSION_IN_PROGRESS', payload: false });
                refetchSubscriber();
                refetchSubscribers();
                onClose();
                return true;
            } else if (result === 'selfie') {
                // Handled separately in useEffect for subscriberRelogin
                return false;
            } else if (errorMessages[result]) {
                dispatch({ type: 'SET_RELOGIN_SESSION_IN_PROGRESS', payload: false });
                // showError('Error', errorMessages[result], true);

                enqueueSnackbar(errorMessages[result], {
                    variant: 'error',
                    autoHideDuration: 5000,
                });

                handleCloseDialog(true);
                return true;
            }

            return false;
        },
        [refetchSubscriber, refetchSubscribers, onClose],
    );

    // Handle relogin start
    const handleReloginStart = useCallback(async () => {
        if (!subscriber || !subscriber._id) return;

        if (!selectedOtpOption) {
            const response = await subscriberReloginStart(subscriber._id, email, password, userAgent, cookie, xBc);

            if (response.success && response.session) {
                dispatch({ type: 'SET_SESSION', payload: response.session });

                if (response.session.result) {
                    if (!handleSessionResult(response.session.result)) {
                        dispatch({ type: 'SET_RELOGIN_SESSION_IN_PROGRESS', payload: true });
                    }
                } else {
                    dispatch({ type: 'SET_RELOGIN_SESSION_IN_PROGRESS', payload: true });
                }
            } else {
                dispatch({ type: 'SET_SESSION', payload: null });
                dispatch({ type: 'SET_RELOGIN_SESSION_IN_PROGRESS', payload: false });
                showError('Error', 'Failed to relogin to OnlyFans. Please try again or contact Admin.', true);
            }
        } else {
            if (!otpCode) {
                showError('Error', 'Please enter the 2FA code.');
                return;
            }

            try {
                const response = await subscriberReloginSubmitOtpCode(subscriber._id, selectedOtpOption, otpCode);

                if (!response.success) {
                    // showError('Error', 'Failed to relogin to OnlyFans. Please try again or contact Admin.');

                    enqueueSnackbar('Failed to relogin to OnlyFans. Please try again or contact Admin.', {
                        variant: 'error',
                        autoHideDuration: 5000,
                    });

                    handleCloseDialog(true);
                }

                if (response.session?.result === 'success') {
                    dispatch({ type: 'SET_RELOGIN_SESSION_IN_PROGRESS', payload: false });
                    await refetchSubscriber();
                    await refetchSubscribers();
                    onClose();
                } else {
                    refetchSubscriberRelogin();
                }
            } catch (error) {
                refetchSubscriberRelogin();
            }
        }
    }, [
        subscriber,
        selectedOtpOption,
        email,
        password,
        userAgent,
        cookie,
        xBc,
        otpCode,
        subscriberReloginStart,
        subscriberReloginSubmitOtpCode,
        handleSessionResult,
        refetchSubscriber,
        refetchSubscribers,
        refetchSubscriberRelogin,
        onClose,
        showError,
    ]);

    // Request OTP code
    const handleRequestOtpCode = useCallback(
        async (otpOption: string) => {
            try {
                const response = await subscriberReloginRequestOtpCode(subscriber._id, otpOption);
                dispatch({ type: 'SET_SESSION', payload: response.session });
            } catch (error) {
                // Handle error
            }
        },
        [subscriber._id, subscriberReloginRequestOtpCode],
    );

    // Update state based on session changes
    useEffect(() => {
        if (subscriberRelogin) {
            dispatch({ type: 'SET_SESSION', payload: subscriberRelogin.session });

            if (subscriberRelogin.running) {
                if (subscriberRelogin.session?.result === 'selfie' && subscriberRelogin.session.otpLink) {
                    dispatch({ type: 'SET_SELFIE_LINK', payload: subscriberRelogin.session.otpLink });
                } else if (!handleSessionResult(subscriberRelogin.session?.result || null)) {
                    dispatch({ type: 'SET_RELOGIN_SESSION_IN_PROGRESS', payload: true });
                }
            } else {
                dispatch({ type: 'SET_RELOGIN_SESSION_IN_PROGRESS', payload: false });
            }
        } else {
            dispatch({ type: 'SET_SESSION', payload: null });
            dispatch({ type: 'SET_RELOGIN_SESSION_IN_PROGRESS', payload: false });
        }
    }, [subscriberRelogin, handleSessionResult]);

    // Store OTP state separately
    const otpState = useMemo(() => {
        if (
            isReloginSessionInProgress &&
            session &&
            session.result === '2fa' &&
            session.loginResponse &&
            session.loginResponse.error.payload.otpState
        ) {
            return session.loginResponse.error.payload.otpState;
        }
        return null;
    }, [isReloginSessionInProgress, session]);

    // Boolean flag for whether OTP is processing
    const processingOtp = !!otpState;

    // Determine if relogin button should be disabled
    const isReloginButtonDisabled =
        (session && !session.result) ||
        ((selectedOtpOption === 'appOtp' || selectedOtpOption === 'phoneOtp' || selectedOtpOption === 'email') && !otpCode) ||
        (!selectedOtpOption && processingOtp) ||
        selfieLink !== null ||
        (loginMode === 'advanced' && (!userAgent || !cookie || !xBc))
            ? true
            : false;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" disableEscapeKeyDown>
            <StyledCardTitleBar title={`Relogin to OnlyFans - ${subscriber.username}`} />
            <DialogContent>
                <DialogContentText>
                    Enter your new email and password to relogin to OnlyFans. These need to be the details of this actual account. The
                    system will not switch over to a linked page. If you haven't changed your email or password, leave both fields blank.
                </DialogContentText>

                <Grid container spacing={1} alignItems={'center'} justifyContent={'center'}>
                    {isSubscriberReloginLoading && (
                        <Grid item xs={12}>
                            <Grid container justifyContent={'center'} sx={{ mt: 1 }}>
                                <Grid item xs={12}>
                                    <Skeleton variant="text" width={200} />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {!isSubscriberReloginLoading && !processingOtp && !selfieLink && (
                        <Grid item xs={12}>
                            <OnlyFansReloginEmailPassword
                                loginMode={loginMode}
                                setLoginMode={mode => dispatch({ type: 'SET_LOGIN_MODE', payload: mode })}
                                email={email}
                                setEmail={val => dispatch({ type: 'SET_EMAIL', payload: val })}
                                password={password}
                                setPassword={val => dispatch({ type: 'SET_PASSWORD', payload: val })}
                                userAgent={userAgent}
                                setUserAgent={val => dispatch({ type: 'SET_USER_AGENT', payload: val })}
                                cookie={cookie}
                                setCookie={val => dispatch({ type: 'SET_COOKIE', payload: val })}
                                xBc={xBc}
                                setXBc={val => dispatch({ type: 'SET_X_BC', payload: val })}
                                loading={isSubscriberReloginStartSubmitting || isReloginSessionInProgress}
                            />
                        </Grid>
                    )}

                    {processingOtp && (
                        <Grid item xs={12}>
                            <OnlyFansOtpSelection
                                selectedOtpOption={selectedOtpOption}
                                setSelectedOtpOption={val => dispatch({ type: 'SET_SELECTED_OTP_OPTION', payload: val })}
                                otpState={otpState}
                                handleRequestOtpCode={handleRequestOtpCode}
                            />
                        </Grid>
                    )}

                    {(selectedOtpOption === 'appOtp' || selectedOtpOption === 'phoneOtp' || selectedOtpOption === 'email') &&
                        processingOtp && (
                            <Grid item xs={12}>
                                <OnlyFansOtpCode
                                    selectedOtpOption={selectedOtpOption}
                                    otpState={otpState}
                                    otpCode={otpCode}
                                    setOtpCode={val => dispatch({ type: 'SET_OTP_CODE', payload: val })}
                                    handleRequestOtpCode={handleRequestOtpCode}
                                    loading={isSubscriberReloginStartSubmitting}
                                />
                            </Grid>
                        )}

                    {selfieLink && (
                        <Grid item xs={12}>
                            <OnlyFansSelfieIDLink selfieIdLink={selfieLink} />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            {isReloginSessionInProgress && <OnlyFansReloginStatus session={session} submitting={isSubscriberReloginStartSubmitting} />}

            <DialogActions
                sx={{
                    backgroundColor: theme.palette.grey[100],
                }}
            >
                <Button
                    color="error"
                    onClick={() => {
                        handleCloseDialog(false);
                    }}
                >
                    {isReloginSessionInProgress ? 'Cancel' : 'Close'}
                </Button>
                <LoadingButton
                    variant="contained"
                    type="submit"
                    disabled={isReloginButtonDisabled}
                    loading={isSubscriberReloginStartSubmitting}
                    onClick={handleReloginStart}
                >
                    <span>Relogin</span>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default OnlyFansReloginDialog;
